/**
 * Lädt den Produkt-Aktivitäts-Feed (C3) aus den Content-Block-Versionen.
 *
 * Sammelt Master- + Channel-Block-Refs, lädt je Block die volle Versionshistorie
 * (geteilter ['versions','content_block',id]-Cache mit dem Feld-Dropdown) und baut
 * daraus den chronologischen, gruppierten Feed.
 */

import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { getContentBlockVersions } from '@/lib/api/versions'
import { buildEntries, groupEntries, type BlockRef } from './activityFeed'
import type { ContentBlockVersion } from '@/types/versioning'
import type { ContentBlock } from '@/types/content-blocks'
import type { ChannelPackage } from '@/types/channel-packages'

export function useProductActivity(
  masterBlocks: ContentBlock[],
  channelBlocksByPkg: Record<string, ContentBlock[]>,
  packages: ChannelPackage[],
  masterName: string,
) {
  const refs: BlockRef[] = useMemo(() => {
    const list: BlockRef[] = masterBlocks.map((b) => ({
      blockId: b.id,
      blockType: b.block_type,
      scopeKey: null,
      channelName: masterName,
    }))
    for (const pkg of packages) {
      const name = pkg.channel?.name ?? pkg.id
      for (const b of channelBlocksByPkg[pkg.id] ?? []) {
        list.push({ blockId: b.id, blockType: b.block_type, scopeKey: pkg.id, channelName: name })
      }
    }
    return list
  }, [masterBlocks, channelBlocksByPkg, packages, masterName])

  const results = useQueries({
    queries: refs.map((r) => ({
      queryKey: ['versions', 'content_block', r.blockId],
      queryFn: () => getContentBlockVersions(r.blockId),
      staleTime: 30_000,
    })),
  })

  const versionsByBlock: Record<string, ContentBlockVersion[]> = {}
  refs.forEach((r, i) => {
    versionsByBlock[r.blockId] = results[i]?.data?.versions ?? []
  })

  const entries = useMemo(() => buildEntries(refs, versionsByBlock), [refs, versionsByBlock])
  const groups = useMemo(() => groupEntries(entries), [entries])

  return { groups, entries, isLoading: results.some((r) => r.isLoading) }
}
